import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Paper,
  TextField,
  Typography,
  MenuItem,
  Divider,
  Button,
  Switch,
  FormControlLabel,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { get, post, put } from '../../services/https';
import { useNavigate, useParams } from 'react-router-dom';
import useSnackbar from '../../hooks/useSnackbar';
import { DetailPageHeader } from '../layout/DetailPageHeader';
import {
  PRODUCT_CATEGORY_OPTIONS,
  PRODUCT_TYPE_OPTIONS_BY_CATEGORY,
} from '../../utils/constants';

const getInitialFormData = () => ({
  name: '',
  description: '',
  category: 'AC',
  subCategory: 'SPLIT_AC',
  brand: '',
  model: '',
  modelSeries: '',
  indoorModelNo: '',
  outdoorModelNo: '',
  manufacturer: '',
  mrp: '',
  discountedPrice: '',
  contractorPointPrice: '',
  stock: '',
  minOrderQty: '1',
  productType: '',
  tonnage: '',
  inverter: false,
  powerConsumption: '',
  refrigerant: '',
  voltage: '',
  productWarranty: '',
  compressorWarranty: '',
  featured: false,
  active: true,
  offerLabel: '',
  discountedPercentage: '',
  sku: '',
  customerPrice: '',
  manufacturingYear: '',
  copperKit: '',
  seer: '',
  energyLabel: '',
  annualEnergyConsumptionCooling: '',
  annualEnergyConsumptionHeating: '',
  powerRequirement: '',
  powerSupply: '',
  ratedCurrent: '',
  runningCurrent: '',
  startingCurrent: '',
  maxFuseSize: '',
  coolingCapacity: '',
  ukTotalCooling: '',
  ukSensibleCooling: '',
  nominalCooling: '',
  nominalHeating: '',
  compressorType: '',
  ambientTemperature: '',
  convertibleMode: '',
  condenserCoil: '',
  coverageArea: '',
  airFlowDirection: '',
  airFlowVolume: '',
  dustFilters: '',
  antiBacteria: '',
  indoorDimensions: '',
  outdoorDimensions: '',
  indoorUnitWeight: '',
  outdoorUnitWeight: '',
  bodyMaterial: '',
  colour: '',
  communicationCableSize: '',
  connectingPipeLength: '',
  copperPipeSize: '',
  remoteControl: '',
  sleepMode: '',
  autoRestart: '',
  selfDiagnosis: '',
  timer: '',
  display: '',
  wiFiConnectivity: '',
  soundLevel: '',
  quietMode: '',
  specialFeatures: '',
  warranty: '',
  warrantyType: '',
});

const AddProduct: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { showSnackbar, SnackbarComponent } = useSnackbar();
  const isEditMode = Boolean(id);
  const [loading, setLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(isEditMode);
  const [uploading, setUploading] = useState(false);
  const [images, setImages] = useState<string[]>([]);
  const [selectedImage, setSelectedImage] = useState(0);

  const [formData, setFormData] = useState(getInitialFormData());

  useEffect(() => {
    if (!isEditMode || !id) return;

    const fetchProduct = async () => {
      try {
        setIsInitialLoading(true);
        const res = await get(`/api/v1/admin/shop/product/${id}`);
        const product = res.data?.data;
        if (!product) return;

        const pricing = product.pricing || {};
        const specs = product.specifications || {};

        setImages(product.images || []);
        setFormData({
          ...getInitialFormData(),
          name: product.name || '',
          description: product.description || '',
          category: product.category || 'AC',
          subCategory: product.subCategory || 'SPLIT_AC',
          brand: product.brand || '',
          model: product.model || '',
          modelSeries: product.modelSeries || '',
          indoorModelNo: product.indoorModelNo || '',
          outdoorModelNo: product.outdoorModelNo || '',
          manufacturer: product.manufacturer || '',
          mrp: pricing.retailPrice?.toString() || pricing.mrp?.toString() || '',
          discountedPrice: pricing.discountedPrice?.toString() || '',
          contractorPointPrice: pricing.contractorPointPrice?.toString() || '',
          stock: product.stock?.toString() || '',
          minOrderQty: product.minOrderQty?.toString() || '1',
          productType: specs.type || specs.acType || '',
          tonnage: specs.tonnage?.toString() || '',
          inverter: Boolean(specs.inverter),
          powerConsumption: specs.powerConsumption?.toString() || '',
          refrigerant: specs.refrigerant || '',
          voltage: specs.voltage?.toString() || '',
          productWarranty: specs.warranty?.product || '',
          compressorWarranty: specs.warranty?.compressor || '',
          featured: Boolean(product.featured),
          active: product.active !== undefined ? Boolean(product.active) : true,
          offerLabel: product.offerLabel || '',
          discountedPercentage: pricing.discountedPercentage?.toString() || '',
          sku: product.sku || '',
          customerPrice:
            pricing.customerPrice?.toString() ||
            product.customerPrice?.toString() ||
            '',
          manufacturingYear: specs.manufacturingYear?.toString() || '',
          copperKit: specs.copperKit?.toString() || '',
          seer: specs.seer?.toString() || specs.iseer?.toString() || '',
          energyLabel: specs.energyLabel?.toString() || '',
          annualEnergyConsumptionCooling:
            specs.annualEnergyConsumptionCooling?.toString() || '',
          annualEnergyConsumptionHeating:
            specs.annualEnergyConsumptionHeating?.toString() || '',
          powerRequirement: specs.powerRequirement?.toString() || '',
          powerSupply: specs.powerSupply?.toString() || '',
          ratedCurrent: specs.ratedCurrent?.toString() || '',
          runningCurrent: specs.runningCurrent?.toString() || '',
          startingCurrent: specs.startingCurrent?.toString() || '',
          maxFuseSize: specs.maxFuseSize?.toString() || '',
          coolingCapacity: specs.coolingCapacity?.toString() || '',
          ukTotalCooling: specs.ukTotalCooling?.toString() || '',
          ukSensibleCooling: specs.ukSensibleCooling?.toString() || '',
          nominalCooling: specs.nominalCooling?.toString() || '',
          nominalHeating: specs.nominalHeating?.toString() || '',
          compressorType: specs.compressorType?.toString() || '',
          ambientTemperature: specs.ambientTemperature?.toString() || '',
          convertibleMode: specs.convertibleMode?.toString() || '',
          condenserCoil: specs.condenserCoil?.toString() || '',
          coverageArea: specs.coverageArea?.toString() || '',
          airFlowDirection: specs.airFlowDirection?.toString() || '',
          airFlowVolume: specs.airFlowVolume?.toString() || '',
          dustFilters: specs.dustFilter?.toString() || '',
          antiBacteria: specs.antiBacteria?.toString() || '',
          indoorDimensions: specs.indoorUnitDimensions?.toString() || '',
          outdoorDimensions: specs.outdoorUnitDimensions?.toString() || '',
          indoorUnitWeight: specs.indoorUnitWeight?.toString() || '',
          outdoorUnitWeight: specs.outdoorUnitWeight?.toString() || '',
          bodyMaterial: specs.bodyMaterial?.toString() || '',
          colour: specs.color?.toString() || '',
          communicationCableSize:
            specs.communicationCableSize?.toString() || '',
          connectingPipeLength: specs.connectingPipeLength?.toString() || '',
          copperPipeSize: specs.copperPipeSize?.toString() || '',
          remoteControl: specs.remoteControl?.toString() || '',
          sleepMode: specs.sleepMode?.toString() || '',
          autoRestart: specs.autoRestart?.toString() || '',
          selfDiagnosis: specs.selfDiagnosis?.toString() || '',
          timer: specs.timer?.toString() || '',
          display: specs.display?.toString() || '',
          wiFiConnectivity: specs.wifiConnectivity?.toString() || '',
          soundLevel: specs.noiseLevel?.toString() || '',
          quietMode: specs.quietMode?.toString() || '',
          specialFeatures: specs.specialFeatures?.toString() || '',
          warranty:
            specs.warranty?.product?.toString() ||
            specs.warranty?.toString() ||
            '',
          warrantyType: specs.warrantyType?.toString() || '',
        });
      } catch (error) {
        console.error('Failed to load product details', error);
        showSnackbar('Failed to load product details', 'error');
      } finally {
        setIsInitialLoading(false);
      }
    };

    fetchProduct();
  }, [id, isEditMode]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    // ✅ All numeric fields
    const numberFields = [
      'mrp',
      'discountedPrice',
      'customerPrice',
      'contractorPointPrice',
      'stock',
      'minOrderQty',
      'discountedPercentage',
      'tonnage',
      'discount',
      'minValue',
      'seer',
      'powerConsumption',
    ];

    let updatedValue: any = value;

    if (numberFields.includes(name)) {
      // Keep only digits and a single decimal point ("-", "e", pasted text
      // would otherwise blank the controlled value and drop the label onto
      // the still-visible input text).
      updatedValue = value.replace(/[^0-9.]/g, '');
      const firstDot = updatedValue.indexOf('.');
      if (firstDot !== -1) {
        updatedValue =
          updatedValue.slice(0, firstDot + 1) +
          updatedValue.slice(firstDot + 1).replace(/\./g, '');
      }

      // 🔥 Remove leading zeros ("012" → "12")
      updatedValue = updatedValue.replace(/^0+(?=\d)/, '');
    }

    setFormData((prev) => {
      if (name === 'category') {
        // The Type dropdown is scoped per category — clear a productType
        // that doesn't belong to the newly selected category.
        const validTypes = (PRODUCT_TYPE_OPTIONS_BY_CATEGORY[updatedValue] || []).map(
          (o) => o.value,
        );
        return {
          ...prev,
          category: updatedValue,
          productType: validTypes.includes(prev.productType)
            ? prev.productType
            : '',
        };
      }
      return {
        ...prev,
        [name]: updatedValue,
      };
    });
  };

  // Blocks "-"/"+"/"e" (which type="number" inputs report as "" while still
  // painting it, un-shrinking the MUI label) and Up/Down (which the browser
  // otherwise uses to increment/decrement type="number" fields) — paired
  // with noSpinnerSx below to fully remove the accidental increment/decrement
  // behaviour on fields where staff type a value directly (price/measurement
  // fields).
  const blockSpinnerKeys = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (
      ['-', '+', 'e', 'E', 'ArrowUp', 'ArrowDown'].includes(
        (e as React.KeyboardEvent).key,
      )
    ) {
      e.preventDefault();
    }
  };

  // Hides the native up/down spinner control on type="number" inputs.
  const noSpinnerSx = {
    '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button':
      {
        WebkitAppearance: 'none',
        margin: 0,
      },
    '& input[type=number]': {
      MozAppearance: 'textfield',
    },
  };

  const handleSwitch =
    (name: 'inverter' | 'featured' | 'active') =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({ ...prev, [name]: e.target.checked }));
    };

  // ================= IMAGE UPLOAD =================
  const uploadImageToS3 = async (file: File) => {
    try {
      setUploading(true);

      const res = await get(
        '/api/v1/admin/shop/product/generate-presigned-url',
        {
          params: {
            fileName: file.name,
            fileType: file.type,
            type: 9,
          },
        },
      );

      if (!res.data?.status) throw new Error('Presigned URL failed');

      const presignedUrl = res.data.presignedUrl;

      await fetch(presignedUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': file.type,
        },
        body: file,
      });

      const imageUrl = presignedUrl.split('?')[0];

      setImages((prev) => {
        const updated = [...prev, imageUrl];
        setSelectedImage(updated.length - 1);
        console.log('Uploaded Images Array:', updated);
        return updated;
      });
    } catch (error) {
      console.error(error);
      showSnackbar('Image upload failed', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;

    const selectedFiles = Array.from(e.target.files);

    if (images.length + selectedFiles.length > 5) {
      showSnackbar('You can upload maximum 5 images', 'error');
      return;
    }

    selectedFiles.forEach((file) => uploadImageToS3(file));
  };

  const handleRemoveImage = (index: number) => {
    setImages((prev) => {
      const updated = prev.filter((_, i) => i !== index);
      if (updated.length === 0) {
        setSelectedImage(0);
      } else if (selectedImage >= updated.length) {
        setSelectedImage(updated.length - 1);
      }
      return updated;
    });
  };

  const handlePrevImage = () => {
    if (!images.length) return;
    setSelectedImage((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNextImage = () => {
    if (!images.length) return;
    setSelectedImage((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  // Generate a unique SKU
  const generateSKU = () => {
    const timestamp = Date.now().toString(36);
    const random = Math.random().toString(36).substring(2, 8);
    return `PROD-${timestamp}-${random}`.toUpperCase();
  };

  // ================= SUBMIT =================
  const handleSubmit = async () => {
    if (images.length < 1) {
      showSnackbar('Please upload at least 1 product image', 'error');
      return;
    }

    try {
      setLoading(true);

      // Generate SKU if not provided
      const sku = formData.sku || generateSKU();

      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        images,
        category: formData.category,
        subCategory: formData.subCategory,
        brand: formData.brand.trim(),
        model: formData.model.trim(),
        modelSeries: formData.modelSeries.trim(),
        indoorModelNo: formData.indoorModelNo.trim(),
        outdoorModelNo: formData.outdoorModelNo.trim(),
        manufacturer: formData.manufacturer.trim(),
        pricing: {
          retailPrice: Number(formData.mrp),
          discountedPrice: formData.discountedPrice
            ? Number(formData.discountedPrice)
            : 0,
          customerPrice: formData.customerPrice
            ? Number(formData.customerPrice)
            : 0,
          contractorPointPrice: formData.contractorPointPrice
            ? Number(formData.contractorPointPrice)
            : 0,
          discountedPercentage: formData.discountedPercentage
            ? Number(formData.discountedPercentage)
            : 0,
        },
        stock: formData.stock ? Number(formData.stock) : 0,
        minOrderQty: formData.minOrderQty ? Number(formData.minOrderQty) : 1,
        specifications: {
          type: formData.productType,
          tonnage: Number(formData.tonnage),
          inverter: formData.inverter,
          powerConsumption: formData.powerConsumption,
          refrigerant: formData.refrigerant,
          voltage: formData.voltage,
          manufacturingYear: formData.manufacturingYear,
          copperKit: formData.copperKit,
          seer: formData.seer,
          energyLabel: formData.energyLabel,
          annualEnergyConsumptionCooling:
            formData.annualEnergyConsumptionCooling,
          annualEnergyConsumptionHeating:
            formData.annualEnergyConsumptionHeating,
          powerRequirement: formData.powerRequirement,
          powerSupply: formData.powerSupply,
          ratedCurrent: formData.ratedCurrent,
          runningCurrent: formData.runningCurrent,
          startingCurrent: formData.startingCurrent,
          maxFuseSize: formData.maxFuseSize,
          coolingCapacity: formData.coolingCapacity,
          ukTotalCooling: formData.ukTotalCooling,
          ukSensibleCooling: formData.ukSensibleCooling,
          nominalCooling: formData.nominalCooling,
          nominalHeating: formData.nominalHeating,
          compressorType: formData.compressorType,
          ambientTemperature: formData.ambientTemperature,
          convertibleMode: formData.convertibleMode,
          condenserCoil: formData.condenserCoil,
          coverageArea: formData.coverageArea,
          airFlowDirection: formData.airFlowDirection,
          airFlowVolume: formData.airFlowVolume,
          dustFilter: formData.dustFilters,
          antiBacteria: formData.antiBacteria,
          indoorUnitDimensions: formData.indoorDimensions,
          outdoorUnitDimensions: formData.outdoorDimensions,
          indoorUnitWeight: formData.indoorUnitWeight,
          outdoorUnitWeight: formData.outdoorUnitWeight,
          bodyMaterial: formData.bodyMaterial,
          color: formData.colour,
          communicationCableSize: formData.communicationCableSize,
          connectingPipeLength: formData.connectingPipeLength,
          copperPipeSize: formData.copperPipeSize,
          remoteControl: formData.remoteControl,
          sleepMode: formData.sleepMode,
          autoRestart: formData.autoRestart,
          selfDiagnosis: formData.selfDiagnosis,
          timer: formData.timer,
          display: formData.display,
          wifiConnectivity: formData.wiFiConnectivity,
          noiseLevel: formData.soundLevel,
          quietMode: formData.quietMode,
          specialFeatures: formData.specialFeatures,
          warranty: {
            product: formData.warranty || formData.productWarranty,
            compressor: formData.compressorWarranty,
          },
          warrantyType: formData.warrantyType,
        },
        featured: formData.featured,
        active: formData.active,
        // Include SKU in payload
        sku,
        // Include additional fields if your backend expects them
        offerLabel: formData.offerLabel,
        customerPrice: formData.customerPrice
          ? Number(formData.customerPrice)
          : 0,
      };

      console.log('Submitting payload:', payload);

      if (isEditMode && id) {
        await put(`/api/v1/admin/shop/product/update/${id}`, payload);
      } else {
        await post('/api/v1/admin/shop/product/create', payload);
      }

      showSnackbar(
        isEditMode
          ? 'Product updated successfully'
          : 'Product created successfully',
        'success',
      );
      navigate(-1);
    } catch (err: any) {
      console.error(
        isEditMode ? 'Error updating product:' : 'Error creating product:',
        err,
      );
      if (err.response?.data?.message) {
        showSnackbar(
          `Failed to ${isEditMode ? 'update' : 'create'} product: ${
            err.response.data.message
          }`,
          'error',
        );
      } else {
        showSnackbar(
          `Failed to ${isEditMode ? 'update' : 'create'} product. Please try again.`,
          'error',
        );
      }
    } finally {
      setLoading(false);
    }
  };

  if (isInitialLoading) {
    return (
      <Box>
        {SnackbarComponent}
        <DetailPageHeader title={isEditMode ? 'Edit Product' : 'Add Product'} />
        <Box sx={{ p: 3 }}>
          <Typography color="white">Loading product details...</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      {SnackbarComponent}
      <DetailPageHeader title={isEditMode ? 'Edit Product' : 'Add Product'} />
      <Box sx={{ p: 3 }}>
        <Paper
          sx={{
            p: 3,
            borderRadius: 3,
            background: '#171717',
            border: '1px solid #242424',
            color: '#fff',
            '& .MuiInputLabel-root': {
              color: '#9d9d9d',
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: '#d0d0d0',
            },
            '& .MuiInputLabel-shrink': {
              backgroundColor: '#171717',
              paddingInline: '6px',
              transform: 'translate(14px, -9px) scale(0.75)',
            },
            '& .MuiOutlinedInput-root': {
              color: '#fff',
              backgroundColor: '#171717',
              borderRadius: 1.5,
              '& fieldset': { borderColor: '#424242' },
              '&:hover fieldset': { borderColor: '#5a5a5a' },
              '&.Mui-focused fieldset': { borderColor: '#7B1FA2' },
            },
            '& .MuiInputBase-input': {
              paddingTop: '12px',
              paddingBottom: '12px',
            },
            '& .MuiSvgIcon-root': { color: '#b8b8b8' },
          }}
        >
          <Grid
            container
            spacing={2}
            sx={{
              background: '#1f1f1f',
              p: 2,
              borderRadius: 2,
              border: '1px solid #2b2b2b',
              mb: 2.5,
            }}
          >
            <Grid item xs={12} md={8}>
              <Grid container spacing={1.5}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Product Name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    select
                    fullWidth
                    label="Category"
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                  >
                    {PRODUCT_CATEGORY_OPTIONS.map((c) => (
                      <MenuItem key={c} value={c}>
                        {c}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Brand"
                    name="brand"
                    value={formData.brand}
                    onChange={handleChange}
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Model"
                    name="model"
                    value={formData.model}
                    onChange={handleChange}
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Model Series"
                    name="modelSeries"
                    value={formData.modelSeries}
                    onChange={handleChange}
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Indoor Model No"
                    name="indoorModelNo"
                    value={formData.indoorModelNo}
                    onChange={handleChange}
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Outdoor Model No"
                    name="outdoorModelNo"
                    value={formData.outdoorModelNo}
                    onChange={handleChange}
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Manufacturer"
                    name="manufacturer"
                    value={formData.manufacturer}
                    onChange={handleChange}
                  />
                </Grid>

                {/* <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="SKU (Leave empty for auto-generate)"
                  name="sku"
                  value={formData.sku}
                  onChange={handleChange}
                  helperText="Auto-generates if left empty"
                />
              </Grid> */}

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                  />
                </Grid>

                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    label="Retail Price"
                    name="mrp"
                    value={formData.mrp}
                    onChange={handleChange}
                    type="number"
                    onWheel={(e) => (e.target as HTMLElement).blur()}
                    onKeyDown={blockSpinnerKeys}
                    inputProps={{ min: 0 }}
                    sx={noSpinnerSx}
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    label="Discount Price"
                    name="discountedPrice"
                    value={formData.discountedPrice}
                    onChange={handleChange}
                    type="number"
                    onWheel={(e) => (e.target as HTMLElement).blur()}
                    onKeyDown={blockSpinnerKeys}
                    inputProps={{ min: 0 }}
                    sx={noSpinnerSx}
                  />
                </Grid>
                {/* <Grid item xs={3}>
                <TextField
                  fullWidth
                  label="Offer Label"
                  name="offerLabel"
                  value={formData.offerLabel}
                  onChange={handleChange}
                />
              </Grid> */}

                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    label="Customer Price"
                    name="customerPrice"
                    value={formData.customerPrice}
                    onChange={handleChange}
                    type="number"
                    onWheel={(e) => (e.target as HTMLElement).blur()}
                    onKeyDown={blockSpinnerKeys}
                    inputProps={{ min: 0 }}
                    sx={noSpinnerSx}
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    label="Contractor Price"
                    name="contractorPointPrice"
                    value={formData.contractorPointPrice}
                    onChange={handleChange}
                    type="number"
                    onWheel={(e) => (e.target as HTMLElement).blur()}
                    onKeyDown={blockSpinnerKeys}
                    inputProps={{ min: 0 }}
                    sx={noSpinnerSx}
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    label="Stock"
                    name="stock"
                    value={formData.stock}
                    onChange={handleChange}
                    type="number"
                    onWheel={(e) => (e.target as HTMLElement).blur()}
                    onKeyDown={blockSpinnerKeys}
                    inputProps={{ min: 0 }}
                    sx={noSpinnerSx}
                  />
                </Grid>

                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    label="Minimum Order Quantity"
                    name="minOrderQty"
                    value={formData.minOrderQty}
                    onChange={handleChange}
                    type="number"
                    onWheel={(e) => (e.target as HTMLElement).blur()}
                    onKeyDown={blockSpinnerKeys}
                    inputProps={{ min: 1 }}
                    sx={noSpinnerSx}
                  />
                </Grid>

                {/* <Grid item xs={3}>
                <TextField
                  fullWidth
                  label="Discount Percentage"
                  name="discountedPercentage"
                  value={formData.discountedPercentage}
                  onChange={handleChange}
                  type="number"
                  onWheel={(e) => (e.target as HTMLElement).blur()}
                  onKeyDown={blockInvalidNumberKeys}
                  inputProps={{ min: 0, max: 100 }}
                />
              </Grid> */}
              </Grid>
            </Grid>

            {/* IMAGE UPLOAD */}
            <Grid item xs={8} md={3.75}>
              <Box
                sx={{
                  border: '1px dashed #585858',
                  p: 2,
                  borderRadius: 2,
                  textAlign: 'center',
                  height: '100%',
                  background: '#171717',
                }}
              >
                <CloudUploadIcon fontSize="large" />
                <Typography color="white" mb={1}>
                  Upload Images (1–5)
                </Typography>

                <Button
                  component="label"
                  variant="outlined"
                  disabled={uploading}
                  sx={{ color: '#fff', borderColor: '#5a5a5a' }}
                >
                  {uploading ? 'Uploading...' : 'Select Images'}
                  <input
                    hidden
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageSelect}
                  />
                </Button>

                <Box mt={2}>
                  {images.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No images uploaded yet
                    </Typography>
                  ) : (
                    <Box>
                      <Box
                        sx={{
                          position: 'relative',
                          borderRadius: 2,
                          overflow: 'hidden',
                          border: '1px solid #3e3e3e',
                          background: '#0f0f0f',
                        }}
                      >
                        <Box
                          component="img"
                          src={images[selectedImage]}
                          alt="product"
                          sx={{
                            width: '100%',
                            height: 180,
                            objectFit: 'cover',
                          }}
                        />
                        {images.length > 1 ? (
                          <>
                            <Button
                              onClick={handlePrevImage}
                              sx={{
                                minWidth: 28,
                                width: 28,
                                height: 28,
                                p: 0,
                                borderRadius: '50%',
                                position: 'absolute',
                                left: 8,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                background: 'rgba(0,0,0,0.55)',
                              }}
                            >
                              <ChevronLeftIcon />
                            </Button>
                            <Button
                              onClick={handleNextImage}
                              sx={{
                                minWidth: 28,
                                width: 28,
                                height: 28,
                                p: 0,
                                borderRadius: '50%',
                                position: 'absolute',
                                right: 8,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                background: 'rgba(0,0,0,0.55)',
                              }}
                            >
                              <ChevronRightIcon />
                            </Button>
                          </>
                        ) : null}
                        {isEditMode ? (
                          <Button
                            color="error"
                            variant="contained"
                            size="small"
                            onClick={() => handleRemoveImage(selectedImage)}
                            sx={{
                              position: 'absolute',
                              right: 8,
                              bottom: 8,
                              minWidth: 'auto',
                              px: 1,
                              fontSize: 11,
                            }}
                          >
                            Remove
                          </Button>
                        ) : null}
                      </Box>
                      {images.length > 1 ? (
                        <Box
                          sx={{
                            mt: 1,
                            display: 'flex',
                            gap: 1,
                            overflowX: 'auto',
                            pb: 0.5,
                          }}
                        >
                          {images.map((img, i) => (
                            <Box
                              key={i}
                              component="img"
                              src={img}
                              alt={`thumb-${i}`}
                              onClick={() => setSelectedImage(i)}
                              sx={{
                                width: 52,
                                height: 52,
                                borderRadius: 1,
                                objectFit: 'cover',
                                cursor: 'pointer',
                                border:
                                  selectedImage === i
                                    ? '2px solid #7B1FA2'
                                    : '1px solid #4d4d4d',
                                flexShrink: 0,
                              }}
                            />
                          ))}
                        </Box>
                      ) : null}
                    </Box>
                  )}
                </Box>
              </Box>
            </Grid>
          </Grid>
          <Typography variant="h6" color="white" sx={{ mt: 2.5, mb: 1.5 }}>
            AC Details{' '}
          </Typography>
          <Divider sx={{ mb: 1 }} />
          <Typography variant="inherit" color="white" sx={{ mb: 1.5 }}>
            Key Feature
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <TextField
                select
                fullWidth
                label="Type"
                name="productType"
                value={formData.productType}
                onChange={handleChange}
                disabled={
                  !(PRODUCT_TYPE_OPTIONS_BY_CATEGORY[formData.category] || [])
                    .length
                }
              >
                {(PRODUCT_TYPE_OPTIONS_BY_CATEGORY[formData.category] || [])
                  .length ? (
                  PRODUCT_TYPE_OPTIONS_BY_CATEGORY[formData.category].map(
                    (option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ),
                  )
                ) : (
                  <MenuItem value="" disabled>
                    Not applicable for this category
                  </MenuItem>
                )}
              </TextField>
            </Grid>

            <Grid item xs={3}>
              <TextField
                fullWidth
                label="Tonnage"
                name="tonnage"
                value={formData.tonnage}
                onChange={handleChange}
                type="number"
                onWheel={(e) => (e.target as HTMLElement).blur()}
                onKeyDown={blockSpinnerKeys}
                inputProps={{ step: '0.5', min: 0 }}
                sx={noSpinnerSx}
              />
            </Grid>
            {/* <Grid item xs={3}>
            <TextField
              fullWidth
              label="Power Consumption"
              name="powerConsumption"
              value={formData.powerConsumption}
              onChange={handleChange}
            />
          </Grid> */}
            <Grid item xs={3}>
              <TextField
                select
                fullWidth
                label="Manufacturing Year"
                name="manufacturingYear"
                value={formData.manufacturingYear}
                onChange={handleChange}
              >
                {Array.from(
                  { length: new Date().getFullYear() - 1980 + 1 },
                  (_, i) => new Date().getFullYear() - i,
                ).map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={3}>
              <TextField
                fullWidth
                label="Copper Kit"
                name="copperKit"
                value={formData.copperKit}
                onChange={handleChange}
              />
            </Grid>
            {/* <Grid item xs={3}>
            <TextField
              fullWidth
              label="Refrigerant Type"
              name="refrigerant"
              value={formData.refrigerant}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={2}>
            <TextField
              fullWidth
              label="Voltage"
              name="voltage"
              value={formData.voltage}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={3}>
            <TextField
              fullWidth
              label="Product Warranty"
              name="productWarranty"
              value={formData.productWarranty}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={3}>
            <TextField
              fullWidth
              label="Compressor Warranty"
              name="compressorWarranty"
              value={formData.compressorWarranty}
              onChange={handleChange}
            />
          </Grid> */}

            <Grid item xs={3}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.inverter}
                    onChange={handleSwitch('inverter')}
                  />
                }
                label="Inverter"
              />
            </Grid>

            <Grid item xs={3}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.featured}
                    onChange={handleSwitch('featured')}
                  />
                }
                label="Featured"
              />
            </Grid>

            <Grid item xs={3}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.active}
                    onChange={handleSwitch('active')}
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>

          <Typography variant="inherit" color="white" sx={{ mt: 2, mb: 1.5 }}>
            Energy Efficiency
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <TextField
                fullWidth
                label="SEER (Energy efficiency)"
                name="seer"
                value={formData.seer}
                onChange={handleChange}
                type="number"
                onWheel={(e) => (e.target as HTMLElement).blur()}
                onKeyDown={blockSpinnerKeys}
                inputProps={{ step: '0.5', min: 0 }}
                sx={noSpinnerSx}
              />
            </Grid>

            <Grid item xs={3}>
              <TextField
                fullWidth
                label="Power Consumption / Watt  "
                name="powerConsumption"
                value={formData.powerConsumption}
                onChange={handleChange}
                type="number"
                onWheel={(e) => (e.target as HTMLElement).blur()}
                onKeyDown={blockSpinnerKeys}
                inputProps={{ step: '0.5', min: 0 }}
                sx={noSpinnerSx}
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                fullWidth
                label="Energy Label"
                name="energyLabel"
                value={formData.energyLabel}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                fullWidth
                label="Annual Cooling Energy Consumption"
                name="annualEnergyConsumptionCooling"
                value={formData.annualEnergyConsumptionCooling}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                fullWidth
                label="Annual Heating Energy Consumption"
                name="annualEnergyConsumptionHeating"
                value={formData.annualEnergyConsumptionHeating}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                fullWidth
                label="Rated Current"
                name="ratedCurrent"
                value={formData.ratedCurrent}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                fullWidth
                label="Running Current"
                name="runningCurrent"
                value={formData.runningCurrent}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                fullWidth
                label="Starting Current"
                name="startingCurrent"
                value={formData.startingCurrent}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                fullWidth
                label="Max Fuse Size"
                name="maxFuseSize"
                value={formData.maxFuseSize}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                fullWidth
                label="Power Requirement / Volt  "
                name="powerRequirement"
                value={formData.powerRequirement}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                fullWidth
                label="Power Supply"
                name="powerSupply"
                value={formData.powerSupply}
                onChange={handleChange}
              />
            </Grid>
          </Grid>

          <Typography variant="inherit" color="white" sx={{ mt: 2, mb: 1.5 }}>
            Cooling Performance
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <TextField
                fullWidth
                label="Cooling Capacity"
                name="coolingCapacity"
                value={formData.coolingCapacity}
                onChange={handleChange}
                type="number"
                onWheel={(e) => (e.target as HTMLElement).blur()}
                onKeyDown={blockSpinnerKeys}
                inputProps={{ step: '0.5', min: 0 }}
                sx={noSpinnerSx}
              />
            </Grid>

            <Grid item xs={3}>
              <TextField
                fullWidth
                label="UK Total Cooling"
                name="ukTotalCooling"
                value={formData.ukTotalCooling}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                fullWidth
                label="UK Sensible Cooling"
                name="ukSensibleCooling"
                value={formData.ukSensibleCooling}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                fullWidth
                label="Nominal Cooling"
                name="nominalCooling"
                value={formData.nominalCooling}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                fullWidth
                label="Nominal Heating"
                name="nominalHeating"
                value={formData.nominalHeating}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={3}>
              <TextField
                fullWidth
                label="Compressor Type"
                name="compressorType"
                value={formData.compressorType}
                onChange={handleChange}
                type="text"
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                fullWidth
                label="Refrigerant"
                name="refrigerant"
                value={formData.refrigerant}
                onChange={handleChange}
                type="text"
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                fullWidth
                label="Ambient Temperature"
                name="ambientTemperature"
                value={formData.ambientTemperature}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                fullWidth
                label="Convertible Mode."
                name="convertibleMode"
                value={formData.convertibleMode}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                fullWidth
                label="Condenser Coil "
                name="condenserCoil"
                value={formData.condenserCoil}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                fullWidth
                label="Coverage Area"
                name="coverageArea"
                value={formData.coverageArea}
                onChange={handleChange}
              />
            </Grid>
          </Grid>

          <Typography variant="inherit" color="white" sx={{ mt: 2, mb: 1.5 }}>
            Air Flow & filtration
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <TextField
                fullWidth
                label="Air Flow Direction "
                name="airFlowDirection"
                value={formData.airFlowDirection}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={3}>
              <TextField
                fullWidth
                label="Air Flow Volume"
                name="airFlowVolume"
                value={formData.airFlowVolume}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                fullWidth
                label="Dust Filters"
                name="dustFilters"
                value={formData.dustFilters}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                fullWidth
                label="Anti-bacteria"
                name="antiBacteria"
                value={formData.antiBacteria}
                onChange={handleChange}
              />
            </Grid>
          </Grid>

          <Typography variant="inherit" color="white" sx={{ mt: 2, mb: 1.5 }}>
            Physical Dimensions & Build
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <TextField
                fullWidth
                label="Indoor Dimensions"
                name="indoorDimensions"
                value={formData.indoorDimensions}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={3}>
              <TextField
                fullWidth
                label="Outdoor Dimensions "
                name="outdoorDimensions"
                value={formData.outdoorDimensions}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={3}>
              <TextField
                fullWidth
                label="Indoor Unit Weight."
                name="indoorUnitWeight"
                value={formData.indoorUnitWeight}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={3}>
              <TextField
                fullWidth
                label="Outdoor Unit Weight "
                name="outdoorUnitWeight"
                value={formData.outdoorUnitWeight}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                fullWidth
                label="Body Material"
                name="bodyMaterial"
                value={formData.bodyMaterial}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                fullWidth
                label="Colour"
                name="colour"
                value={formData.colour}
                onChange={handleChange}
              />
            </Grid>
          </Grid>

          <Typography variant="inherit" color="white" sx={{ mt: 2, mb: 1.5 }}>
            Installation & Hardware
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <TextField
                fullWidth
                label="Communication cable size "
                name="communicationCableSize"
                value={formData.communicationCableSize}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={3}>
              <TextField
                fullWidth
                label="Connecting Pipe Length  "
                name="connectingPipeLength"
                value={formData.connectingPipeLength}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                fullWidth
                label="Copper pipe size"
                name="copperPipeSize"
                value={formData.copperPipeSize}
                onChange={handleChange}
              />
            </Grid>
          </Grid>

          <Typography variant="inherit" color="white" sx={{ mt: 2, mb: 1.5 }}>
            Smart Features & Controls
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <TextField
                fullWidth
                label="Remote Control "
                name="remoteControl"
                value={formData.remoteControl}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={3}>
              <TextField
                fullWidth
                label="Sleep Mode"
                name="sleepMode"
                value={formData.sleepMode}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                fullWidth
                label="Auto Restart"
                name="autoRestart"
                value={formData.autoRestart}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                fullWidth
                label="Self Diagnosis"
                name="selfDiagnosis"
                value={formData.selfDiagnosis}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={3}>
              <TextField
                fullWidth
                label="Timer."
                name="timer"
                value={formData.timer}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                fullWidth
                label="Display"
                name="display"
                value={formData.display}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                fullWidth
                label="Wi‑Fi Connectivity"
                name="wiFiConnectivity"
                value={formData.wiFiConnectivity}
                onChange={handleChange}
              />
            </Grid>
          </Grid>

          <Typography variant="inherit" color="white" sx={{ mt: 2, mb: 1.5 }}>
            Noise & Sound
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <TextField
                fullWidth
                label="Sound Level"
                name="soundLevel"
                value={formData.soundLevel}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                fullWidth
                label="Quiet Mode"
                name="quietMode"
                value={formData.quietMode}
                onChange={handleChange}
              />
            </Grid>
          </Grid>

          <Typography variant="inherit" color="white" sx={{ mt: 2, mb: 1.5 }}>
            Special Features
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={8}>
              <TextField
                fullWidth
                label="Special Features "
                name="specialFeatures"
                value={formData.specialFeatures}
                onChange={handleChange}
              />
            </Grid>
          </Grid>
          <Typography variant="inherit" color="white" sx={{ mt: 2, mb: 1.5 }}>
            Warranty
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <TextField
                fullWidth
                label="Warranty"
                name="warranty"
                value={formData.warranty}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                fullWidth
                label="Warranty Type"
                name="warrantyType"
                value={formData.warrantyType}
                onChange={handleChange}
              />
            </Grid>
          </Grid>

          <Box mt={4} display="flex" justifyContent="flex-end" gap={2}>
            <Button variant="outlined" onClick={() => navigate(-1)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading
                ? 'Saving...'
                : isEditMode
                  ? 'Update Product'
                  : 'Save Product'}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default AddProduct;
